<template>
  <RouterLink
    :to="`/event/${eventItem.id}`"
    :class="[
      'bg-white rounded-md hover:opacity-80 flex flex-col delay-200 duration-500 transition border-surface-grey border',
      list ? 'md:flex-row' : '',
      inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
    ]"
  >
    <div
      ref="inviewRef"
      :id="`event-${eventItem.id}`"
      :class="[
        'aspect-16/9 border-b border-surface-grey relative',
        list
          ? 'md:shrink-0 md:w-90 h-full md:aspect-auto md:rounded-l-md md:rounded-r-none md:border-b-0 md:border-r'
          : '',
      ]"
    >
      <img
        :src="photoToUrl(eventItem.photo)"
        :alt="eventItem.info1"
        :class="[
          'w-full h-full object-cover rounded-t-md aspect-16/9',
          list ? 'md:rounded-l-md md:rounded-t-none md:rounded-r-none' : '',
        ]"
      />
      <div 
        v-if="badges[0]" 
        :class="'absolute top-2 right-2 text-headings text-xs px-2 py-1 rounded font-semibold ' + badges[0].class"
      >
        {{ $t(badges[0].localeKey) }}
      </div>
      <svg
        :class="[
          'absolute z-10 bottom-0 -right-[1px] translate-y-1/2 fill-surface-light stroke-surface-grey stroke-1',
          list
            ? 'md:rotate-90 md:translate-y-0 md:-translate-x-full md:-bottom-[1px] md:right-0 md:origin-bottom-right'
            : '',
        ]"
        xmlns="http://www.w3.org/2000/svg"
        width="7"
        height="16"
        viewBox="0 0 7 16"
      >
        <path
          d="M7.5 0.518555C3.59105 0.775917 0.5 4.02593 0.5 8C0.5 11.9741 3.59105 15.2241 7.5 15.4814V0.518555Z"
        />
      </svg>

      <svg
        :class="[
          'absolute bottom-0 -left-[1px] z-10 translate-y-1/2 fill-surface-light stroke-surface-grey stroke-1',
          list
            ? 'md:rotate-90 md:translate-x-full md:-top-[2px] md:left-auto md:bottom-auto md:-right-[1px] md:origin-top-right'
            : '',
        ]"
        xmlns="http://www.w3.org/2000/svg"
        width="7"
        height="16"
        viewBox="0 0 7 16"
        fill="none"
      >
        <path
          d="M-0.5 0.518555C3.40895 0.775917 6.5 4.02593 6.5 8C6.5 11.9741 3.40895 15.2241 -0.5 15.4814V0.518555Z"
        />
      </svg>
    </div>
    <div :class="['p-4 grow', list ? '' : '']">
      <div
        :class="[
          list
            ? 'md:flex md:justify-between md:border-b md:border-surface-grey md:mb-3'
            : '',
        ]"
      >
        <div :class="list ? 'md:flex-1 ' : ''">
          <h2
            class="h4 mb-2"
            :class="eventNameVisibilityClass"
          >
            {{ getLocalizedEventValue(eventItem, locale, 'info1') }}
          </h2>
          <p
            v-if="eventItem.info2"
            class="text-text text-sm mb-3"
            :class="eventNameVisibilityClass"
          >
            {{ getLocalizedEventValue(eventItem, locale, 'info2') }}
          </p>
        </div>
        <div :class="list ? 'md:text-right md:min-w-42 md:ml-4' : ''">
          <div
            :class="[
              'flex items-center gap-2 mb-2',
              priceVisibilityClass,
              list ? 'md:justify-end md:flex-col md:items-end' : '',
            ]"
          >
            <span class="text-lg font-bold text-headings">{{
              eventItem.price_override_active ? getLocalizedEventValue(eventItem, locale, 'price_override_text') : formatPrice(eventItem.price, eventItem.currency, $i18n.locale)
            }}</span>
            <span
              v-if="priceBefore"
              class="text-sm text-headings line-through font-semibold text-text"
              >{{ $t('common.before') }}
              {{
                formatPrice(
                  priceBefore,
                  eventItem.currency,
                  $i18n.locale
                )
              }}
              </span
            >
          </div>
        </div>
      </div>
      <div
        :class="dateVisibilityClass"
        class="flex items-center text-sm text-headings mb-2"
      >
        <CalendarIcon class="h-4 w-4 mr-2 fill-surface-dark flex-shrink-0" />
        <EventDate :event="eventItem" :showTime="generalStore.customer().frontpage_show_time" class="line-clamp-1" />
      </div>
      <div
        :class="venueVisibilityClass"
        class="flex items-center text-sm text-headings"
      >
        <MapPin class="h-4 w-4 mr-2 fill-surface-dark flex-shrink-0" />
        <span class="line-clamp-1">{{ venue ? venue.name : '' }}</span>
      </div>
      <div
        :class="categoryVisibilityClass"
        class="mt-3 flex flex-wrap gap-2"
      >
        <span
          v-for="(tag, i) in categoryNames"
          :key="i"
          class="inline-block max-w-[200px] min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-xs px-2 py-1 font-semibold rounded border-1 border-surface-grey bg-surface-light text-text"
        >{{ tag }}</span>
      </div>
    </div>
  </RouterLink>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  import { useGeneralStore } from '@/stores/general';
  import type { EventItem } from '@/types';
  import { getBadgesWithVisibility, getCategoryVisibilityClass, getDateVisibilityClass, getEventNameVisibilityClass, getLocalizedEventValue, getPriceVisibilityClass, getVenueVisibilityClass } from '@/utils/eventItemUtil';
  import { formatPrice } from '@/utils/formatPrice';
  import { getLocalizedCategoryValue } from '@/utils/getCategoryLocalization';
  import { photoToUrl } from '@/utils/photoToUrl';
  import { useInView } from '@/utils/useInView';

  import CalendarIcon from '../icons/CalendarIcon.vue';
  import MapPin from '../icons/MapPinIcon.vue';
  import EventDate from './EventDate.vue';

  const { locale, t: $t  } = useI18n();
  
  const props = defineProps<{
    eventItem: EventItem;
    list?: boolean;
  }>();
  
  const { inviewRef, inView } = useInView();

  const generalStore = useGeneralStore();

  const venues = computed(() => generalStore.venues());

  const venue = computed(() =>
    venues.value?.find(v => v.id === props.eventItem.venue)
  );

  const categoryNames = computed(() => {
    const categoryIds = props.eventItem.categories || [];
    const allCategories = generalStore.categories();

    return categoryIds.reduce((names, id) => {
      const category = allCategories.find(category => category.id === id);
      // filter out categories that are not available in the active language
      if (category && category.localizations?.[locale.value]?.['name']) {
        names.push(getLocalizedCategoryValue(category, locale.value, 'name'));
      }
      return names;
    }, [] as string[]);
  });

  const badges = computed(() => getBadgesWithVisibility(props.eventItem));
  
  const priceBefore = computed(() => {
    const usertype = 'jpoplev'; // TODO get from sso module
    return props.eventItem.sso_prices[usertype]?.price;
  }); 

  const categoryVisibilityClass = computed(() => getCategoryVisibilityClass(props.eventItem));
  const dateVisibilityClass = computed(() => getDateVisibilityClass(props.eventItem));
  const eventNameVisibilityClass = computed(() => getEventNameVisibilityClass(props.eventItem, props.list));
  const venueVisibilityClass = computed(() => getVenueVisibilityClass(props.eventItem));
  const priceVisibilityClass = computed(() => getPriceVisibilityClass(props.eventItem));
</script>
